import type { AttributeOffsets } from "../engine/attributeOffsets";
import type { TunedParams } from "../engine/config";
import type { NudgeEntry } from "../engine/tasteNudge";
import type { IsoDateTime } from "../engine/dates";
import { densityClassFrom, processClassFrom, roastLevelFrom, tasteFrom, type Bean, type Grinder, type Shot } from "../engine/model";

/**
 * Persistence seams, mirroring the Java interfaces but asynchronous because IndexedDB is.
 * The engine never sees these; the view model does.
 */
export interface GrinderRepository {
  /** Insert (id === null) or update. Returns the stored grinder with its id. */
  save(grinder: Grinder): Promise<Grinder>;
  findById(id: number): Promise<Grinder | null>;
  /** All grinders, ordered by id. */
  findAll(): Promise<Grinder[]>;
}

export interface BeanRepository {
  save(bean: Bean): Promise<Bean>;
  findById(id: number): Promise<Bean | null>;
  /** Non-archived beans, newest roast first (ties: newest id first). */
  findActive(): Promise<Bean[]>;
  /** Everything, newest roast first. */
  findAll(): Promise<Bean[]>;
  archive(id: number): Promise<void>;
}

export interface ShotRepository {
  save(shot: Shot): Promise<Shot>;
  findById(id: number): Promise<Shot | null>;
  /** All shots for one bean on one grinder, newest first. */
  findByBeanAndGrinder(beanId: number, grinderId: number): Promise<Shot[]>;
  /** All shots for a bean across grinders, newest first. */
  findByBean(beanId: number): Promise<Shot[]>;
  /** Most recent shot on a grinder, any bean. */
  findLatestByGrinder(grinderId: number): Promise<Shot | null>;
  /** All shots, newest first (export). */
  findAll(): Promise<Shot[]>;
  delete(id: number): Promise<void>;
}

export const LAST_GRINDER_ID = "last_grinder_id";
export const LAST_BEAN_ID = "last_bean_id";

export interface SettingsStore {
  get(key: string): Promise<string | null>;
  /** Upsert; null removes the key. */
  put(key: string, value: string | null): Promise<void>;
  /** Every key/value pair (export). */
  entries(): Promise<Record<string, string>>;
}

/** The learned attribute offsets table (V3). */
export interface AttributeOffsetsRepository {
  /** The whole table (seeds when empty). */
  load(): Promise<AttributeOffsets>;
  /** Replace every row with the given table. */
  save(offsets: AttributeOffsets): Promise<void>;
  /** Back to the seed values with n_beans = 0. Callers also clear the learning ledger so learning re-runs. */
  resetToSeeds(): Promise<void>;
}

export interface LearningEntry {
  readonly beanId: number;
  readonly grinderId: number;
  readonly learnedAt: IsoDateTime;
}

/** Which (bean, grinder) pairs have already contributed to the offsets (V3). */
export interface BeanLearningRepository {
  isLearned(beanId: number, grinderId: number): Promise<boolean>;
  markLearned(beanId: number, grinderId: number, learnedAt: IsoDateTime): Promise<void>;
  findAll(): Promise<LearningEntry[]>;
  clear(): Promise<void>;
}

export async function getLongSetting(store: SettingsStore, key: string): Promise<number | null> {
  const v = await store.get(key);
  if (v === null) return null;
  const n = Number.parseInt(v.trim(), 10);
  return Number.isNaN(n) ? null : n;
}

export async function putLongSetting(store: SettingsStore, key: string, value: number | null): Promise<void> {
  await store.put(key, value === null ? null : String(value));
}

/** One row per change of the tuned predictor parameters (V6). Rows are only ever appended. */
export type ConfigVersionKind = "tuned" | "revert" | "reset";

export interface ConfigVersion {
  /** sequence number, 1, 2, … (null before saving) */
  readonly id: number | null;
  /** stamped on shots: "<CONFIG_VERSION>+v<id>" */
  readonly version: string;
  readonly createdAt: IsoDateTime;
  readonly kind: ConfigVersionKind;
  readonly params: TunedParams;
  /** replay scores before and after (tuned rows only) */
  readonly scoreBefore: number | null;
  readonly scoreAfter: number | null;
  /** the version this one replaced */
  readonly parentVersion: string;
}

export interface ConfigVersionRepository {
  save(v: ConfigVersion): Promise<ConfigVersion>;
  /** Oldest first. */
  findAll(): Promise<ConfigVersion[]>;
}

/** A taste nudge on a bean's target time (V6). */
export interface StoredNudge extends NudgeEntry {
  readonly id: number | null;
  readonly beanId: number;
}

export interface TasteNudgeRepository {
  save(n: StoredNudge): Promise<StoredNudge>;
  /** Oldest first. */
  findByBean(beanId: number): Promise<StoredNudge[]>;
  /** Oldest first. */
  findAll(): Promise<StoredNudge[]>;
}

/** Settings keys added in V6. */
export const RECIPE = "recipe";
export const ACTIVE_CONFIG_VERSION = "active_config_version";
export const TUNING_LAST_RUN_SHOTS = "tuning_last_run_shots";
export const TUNING_LAST_RUN_AT = "tuning_last_run_at";
export const FIRST_RUN_DONE = "first_run_done";

/** Everything the app persists, in one object. */
export interface Repositories {
  readonly grinders: GrinderRepository;
  readonly beans: BeanRepository;
  readonly shots: ShotRepository;
  readonly settings: SettingsStore;
  readonly offsets: AttributeOffsetsRepository;
  readonly learning: BeanLearningRepository;
  readonly configVersions: ConfigVersionRepository;
  readonly nudges: TasteNudgeRepository;
  /** Remove every record (used by import in replace mode). Offsets go back to seeds. */
  clearAll(): Promise<void>;
}

/** Seed grinders, identical to the SQLite V1 migration. Placeholders to be recalibrated from data. */
export const SEED_GRINDERS: readonly Omit<Grinder, "id">[] = [
  { name: "La Marzocco Pico", minGrind: 5.0, maxGrind: 10.0, dialResolution: 0.1, priorSlopeB: -0.22, referenceSetting: null, dialFormat: "decimal", dialDirection: "higher_coarser", calibratedSince: null },
  { name: "Niche Zero", minGrind: 10.0, maxGrind: 20.0, dialResolution: 0.1, priorSlopeB: -0.15, referenceSetting: null, dialFormat: "decimal", dialDirection: "higher_coarser", calibratedSince: null },
];

/**
 * Baratza Sette 270Wi, seeded by the V5 migration if no grinder of that name exists. PLACEHOLDERS to recalibrate from
 * data: the slope and the range. On the real dial macro 31 is coarsest and 1 is finest; the manual's espresso range is
 * macro 3–9; the micro ring runs A (fine) to I (coarse) and spans about one macro step. Encoded x = macro +
 * letterIndex/9 (A = 0 … I = 8); the reference 9E = 9 + 4/9 is Baratza's recommended espresso start. Mirrors Java.
 */
export const SETTE_SEED: Omit<Grinder, "id"> = {
  name: "Baratza Sette 270Wi", minGrind: 1.0, maxGrind: 15.0, dialResolution: 1 / 9, priorSlopeB: -0.3,
  referenceSetting: 9 + 4 / 9, dialFormat: "macro_micro", dialDirection: "higher_coarser", calibratedSince: null,
};

// Records written before V3 lack the new fields; every read path normalises them so the engine never sees undefined.

export function normalizeGrinder(g: Grinder): Grinder {
  return {
    ...g, referenceSetting: g.referenceSetting ?? null,
    dialFormat: g.dialFormat === "macro_micro" ? "macro_micro" : "decimal",
    dialDirection: g.dialDirection === "higher_finer" ? "higher_finer" : "higher_coarser", calibratedSince: g.calibratedSince ?? null,
  };
}

export function normalizeBean(b: Bean): Bean {
  return {
    ...b,
    roastLevel: roastLevelFrom(b.roastLevel), processClass: processClassFrom(b.processClass),
    densityClass: densityClassFrom(b.densityClass), decaf: b.decaf ?? false, targetTimeS: b.targetTimeS ?? null,
  };
}

export function normalizeShot(s: Shot): Shot {
  return { ...s, predictedGrind: s.predictedGrind ?? null, predictedSlope: s.predictedSlope ?? null, configVersion: s.configVersion ?? null, taste: tasteFrom(s.taste) };
}

// Shared ordering helpers so every implementation sorts identically.

export function newestShotFirst(a: Shot, b: Shot): number {
  if (a.timestamp !== b.timestamp) return a.timestamp < b.timestamp ? 1 : -1;
  return (b.id ?? 0) - (a.id ?? 0);
}

export function newestRoastFirst(a: Bean, b: Bean): number {
  if (a.roastDate !== b.roastDate) return a.roastDate < b.roastDate ? 1 : -1;
  return (b.id ?? 0) - (a.id ?? 0);
}
